import type { MapGeometry } from "../shared/types";
import { mapStandHeight } from "./venueMapProjection";

type BoundsPoint = { x: number; y: number; z: number };
const boundsCache = new WeakMap<
  MapGeometry,
  {
    points: BoundsPoint[];
    elevation: number;
  }
>();

/** Cache source bounds so rotation and panning do not rescan booth polygons. */
export function venueMapBounds(geometry: MapGeometry) {
  const cached = boundsCache.get(geometry);
  if (cached) return cached;
  const { floor, width, height } = geometry;
  // Retain the small original roof margin around the floor. Taller custom
  // roofs contribute their own extent instead of raising every floor corner.
  const points = [floor.x, floor.x + floor.width].flatMap((x) =>
    [floor.y, floor.y + floor.height].flatMap((y) =>
      [-8, 12].map((z) => ({ x: x / width, y: y / height, z })),
    ),
  );
  let elevation = 12;
  // Equal-height booths share one conservative rectangle; this bounds work
  // during gestures even when a map contains many detailed booth polygons.
  const levels = new Map<
    number,
    { left: number; right: number; top: number; bottom: number }
  >();
  for (const stand of geometry.stands) {
    if (stand.hidden) continue;
    const z = mapStandHeight(stand);
    elevation = Math.max(elevation, z);
    const level = levels.get(z) || {
      left: Infinity,
      right: -Infinity,
      top: Infinity,
      bottom: -Infinity,
    };
    for (const polygon of stand.polygons) {
      for (const [x, y] of polygon) {
        // Ordinary booths are already contained in the floor's roof margin.
        if (
          z <= 12 &&
          x >= floor.x &&
          x <= floor.x + floor.width &&
          y >= floor.y &&
          y <= floor.y + floor.height
        )
          continue;
        level.left = Math.min(level.left, x);
        level.right = Math.max(level.right, x);
        level.top = Math.min(level.top, y);
        level.bottom = Math.max(level.bottom, y);
      }
    }
    if (Number.isFinite(level.left)) levels.set(z, level);
  }
  for (const [z, level] of levels) {
    for (const x of [level.left, level.right]) {
      for (const y of [level.top, level.bottom]) {
        points.push({ x: x / width, y: y / height, z });
      }
    }
  }
  const bounds = { points, elevation };
  boundsCache.set(geometry, bounds);
  return bounds;
}
